import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, FileText, CheckCircle2, Clock, RotateCw, Download, Folder, ChevronDown, ChevronRight, ArrowUpDown } from "lucide-react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";

const dt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PAGE_SIZE = 50;

export default function DesignWork() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // all | pending | approved | rev
  const [sortDir, setSortDir] = useState("desc"); // desc = newest first
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiry/design-work");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) =>
        [r.customer_name, r.name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    if (status === "pending") out = out.filter((r) => !r.drawing_approved_at);
    if (status === "approved") out = out.filter((r) => r.drawing_approved_at);
    if (status === "rev") out = out.filter((r) => (r.revisions_count || 0) > 0);
    // Sort by drawing_received_at (fallback to name for missing dates)
    out = out.slice().sort((a, b) => {
      const ax = new Date(a.drawing_received_at || 0).getTime();
      const bx = new Date(b.drawing_received_at || 0).getTime();
      return sortDir === "desc" ? bx - ax : ax - bx;
    });
    return out;
  }, [rows, q, status, sortDir]);

  // Reset page whenever filters change
  useEffect(() => { setPage(1); }, [q, status, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const totals = data?.totals || { count: 0, pending: 0, approved: 0, revisions: 0 };

  return (
    <DesktopLayout>
      <div className="max-w-[1500px] mx-auto p-3 sm:p-6 space-y-4" data-testid="design-work-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">2D / 3D Design</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">My Work</h1>
            <div className="text-xs text-slate-500 mt-1">
              Every enquiry you have prepared drawings for — with received / approved dates,
              revision count, and downloadable files.
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="dw-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dw-totals">
          <Tile label="Total enquiries" value={totals.count} tone="#0f172a" />
          <Tile label="Pending approval" value={totals.pending} icon={Clock} tone="#c2410c" />
          <Tile label="Sales approved" value={totals.approved} icon={CheckCircle2} tone="#15803d" />
          <Tile label="Total revisions" value={totals.revisions} icon={RotateCw} tone="#7c3aed" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input className="w-full border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm"
              placeholder="Search customer name"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="dw-search" />
          </div>
          <div className="inline-flex rounded-sm overflow-hidden border border-slate-300">
            {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"], ["rev", "With revisions"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setStatus(k)}
                data-testid={`dw-status-${k}`}
                className={`px-3 py-2 text-xs font-bold uppercase ${status === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="dw-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left">
                <Th>Customer</Th>
                <Th>
                  <button type="button" onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    className="inline-flex items-center gap-1 hover:text-slate-800"
                    data-testid="dw-sort-date"
                    title={`Currently sorted ${sortDir === "desc" ? "newest first" : "oldest first"} — click to toggle`}>
                    Drawing received <ArrowUpDown className="w-3 h-3" />
                  </button>
                </Th>
                <Th>Drawing approved</Th>
                <Th align="center">Revisions</Th>
                <Th>Submitted files</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400" data-testid="dw-empty">
                  No enquiries match the current filters.
                </td></tr>
              ) : (
                pageRows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`dw-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900 truncate max-w-[220px]">{r.customer_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 uppercase">{(r.stage || "").replaceAll("_", " ")}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">
                      {dt(r.drawing_received_at)}
                    </td>
                    <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                      {r.drawing_approved_at
                        ? <span className="text-emerald-700 font-bold">{dt(r.drawing_approved_at)}</span>
                        : <span className="text-slate-400">Pending</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.revisions_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-purple-800 bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded-sm font-black text-[11px]">
                          <RotateCw className="w-3 h-3" /> {r.revisions_count}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-[11px]">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {(r.files || []).length === 0 ? (
                        <span className="text-slate-400 text-[11px] italic">No files</span>
                      ) : (
                        <FolderList enquiryId={r.id} files={r.files} />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3 text-xs" data-testid="dw-pagination">
            <span className="text-slate-500">
              Showing <b>{pageStart + 1}</b>–<b>{Math.min(pageStart + PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
            </span>
            <div className="inline-flex items-center gap-1">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 font-bold uppercase"
                data-testid="dw-prev">← Prev</button>
              <span className="px-2 text-slate-600" data-testid="dw-page-indicator">Page {page} / {totalPages}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-40 font-bold uppercase"
                data-testid="dw-next">Next →</button>
            </div>
          </div>
        )}

        <div className="text-[10px] text-slate-400 mt-3">
          Revisions = number of times the Sales owner rejected a drawing and asked for rework.
          Drawing received = when Sales Coord requested drawings.
          Drawing approved = when Sales owner approved the drawings.
        </div>
      </div>
    </DesktopLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`px-3 py-2 font-bold uppercase text-[10px] tracking-wide text-slate-500 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}

function Tile({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" style={tone ? { color: tone } : {}} />}
        <div className="hk-tiny-label">{label}</div>
      </div>
      <div className="text-2xl font-black tracking-tight mt-1"
        style={tone ? { color: tone } : {}}>
        {value}
      </div>
    </div>
  );
}

// Groups the files of a single enquiry by their `folder` field. Files with an
// empty folder show up under "Root". Folders are collapsible with a badge
// showing the file count. Preview shows the folder header only until clicked
// so long lists (like Mr. Ajith's 8 files) stay compact.
function FolderList({ enquiryId, files }) {
  const groups = React.useMemo(() => {
    const out = {};
    for (const f of files || []) {
      const key = (f.folder || "").trim() || "";
      if (!out[key]) out[key] = [];
      out[key].push(f);
    }
    return Object.entries(out).sort(([a], [b]) => {
      if (a === "" && b !== "") return -1;
      if (b === "" && a !== "") return 1;
      return a.localeCompare(b);
    });
  }, [files]);
  return (
    <div className="space-y-1 max-w-[420px]">
      {groups.map(([folder, items]) => (
        <FolderGroup key={folder || "__root"} folder={folder} items={items} enquiryId={enquiryId}
                     defaultOpen={groups.length <= 2 || !folder /* root always open, else collapsed */} />
      ))}
    </div>
  );
}

function FolderGroup({ folder, items, enquiryId, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const label = folder || "Root";
  return (
    <div className="border border-slate-200 rounded-sm bg-slate-50/60">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1 px-2 py-1 text-left hover:bg-slate-100 rounded-sm"
        data-testid={`dw-folder-toggle-${folder || "root"}`}>
        {open ? <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" />
              : <ChevronRight className="w-3 h-3 text-slate-500 shrink-0" />}
        <Folder className={`w-3 h-3 shrink-0 ${folder ? "text-amber-600" : "text-slate-400"}`} />
        <span className="text-[11px] font-bold text-slate-700 truncate flex-1">{label}</span>
        <span className="text-[10px] text-slate-500 font-mono">{items.length}</span>
      </button>
      {open && (
        <ul className="pl-6 pr-2 pb-1 space-y-0.5">
          {items.map((f) => (
            <li key={f.id}>
              <a href={`${backend}/api/enquiry/${enquiryId}/files/${f.id}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 text-[11px] break-all py-0.5"
                data-testid={`dw-file-${f.id}`}
                title={`${f.name}${f.size ? ` (${Math.round(f.size / 1024)} KB)` : ""}`}>
                <FileText className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[280px]">{f.name}</span>
                <Download className="w-3 h-3 shrink-0 text-slate-400" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
